/* @flow */
import cx from 'classnames';
import idx from 'idx';
import * as React from 'react';
import { mountModal } from 'lib/modal';
import { graphql } from 'react-apollo';

import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import LinkConstants from 'constants/LinkConstants';
import OrganizationReferAdminModal from 'organizations/components/OrganizationReferAdminModal';
import ORGANIZATION_TEACHER_UPSELL_QUERY from 'organizations/queries/OrganizationTeacherUpsellQuery.gql';
import OrganizationUpsellHeader from './OrganizationUpsellHeader';
import REQUEST_ACCESS_FOR_PAID_UPSELL_MUTATION from '../mutations/requestAccessForPaidUpsell.gql';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import type {
  OrganizationTeacherUpsell as OrganizationTeacherUpsellQuery,
  OrganizationTeacherUpsellVariables,
} from 'organizations/queries/__generated__/OrganizationTeacherUpsell';
import type {
  RequestAccessForPaidUpsellInput,
  requestAccessForPaidUpsell as requestAccessForPaidUpsellMutation,
} from '../mutations/__generated__/requestAccessForPaidUpsell';

import './styles/UnpaidOrganizationsNull.scss';

type Props = {
  className?: string,
  organizationUuid: string,
  data: ReactApolloData<OrganizationTeacherUpsellQuery>,
  requestAccessForPaidUpsell: RequestAccessForPaidUpsellInput => Promise<{
    data: requestAccessForPaidUpsellMutation,
  }>,
};

type State = {
  submitting: boolean,
};

class OrganizationTeacherUpsell extends React.Component<Props, State> {
  state: State = {
    submitting: false,
  };

  componentDidMount() {
    EventTracking.trackKey('admin_first_onboarding.UnpaidOrganizationsNull.teacher.view');
  }

  mountOrganizationReferAdminModal = () => {
    const utmParams = {
      utm_source: LinkConstants.UTM_SOURCE_TEACHER,
      utm_medium: LinkConstants.UTM_MEDIUM_PRODUCT,
      utm_campaign: LinkConstants.UTM_CAMPAIGN_TEACHER_UPSELL,
    };
    mountModal(
      <OrganizationReferAdminModal
        organizationUuid={this.props.organizationUuid}
        utmParams={utmParams}
      />,
      this
    );
  };

  submitTeacherUpsellRequest = () =>
    (async () => {
      const { organizationUuid, requestAccessForPaidUpsell } = this.props;
      this.setState({ submitting: true });

      const input: RequestAccessForPaidUpsellInput = {
        organizationUuid,
        context: 'unpaid_organization',
        permissionToUseName: false,
      };

      try {
        const response = await requestAccessForPaidUpsell(input);
        const { error } = response.data.requestAccessForPaidUpsell;

        if (error) {
          ToastActions.showErrorToast(error.message);
        } else {
          this.mountOrganizationReferAdminModal();
        }
      } catch (e) {
        console.error('UnpaidOrganizationsNull - error after submitting request', e);
        ToastActions.showErrorToast('Oops, something went wrong');
      }

      this.setState({ submitting: false });
    })();

  renderRequestAccessButton = (hasRequestedPaidAccess: ?boolean) => {
    const requestAccessEventKey =
      'admin_first_onboarding.UnpaidOrganizationsNull.teacher.request_access.click';
    return hasRequestedPaidAccess ? (
      <Button
        color="mascot"
        level="secondary"
        onClick={this.mountOrganizationReferAdminModal}
        eventKey={requestAccessEventKey}>
        Request access
      </Button>
    ) : (
      <Button
        onClick={this.submitTeacherUpsellRequest}
        eventKey={requestAccessEventKey}
        disabled={this.state.submitting}
        data-test="teacher-upsell-button">
        Request access
      </Button>
    );
  };

  render() {
    const { className, data } = this.props;
    const { loading } = data;

    const hasRequestedPaidAccess = idx(
      data,
      _ => _.organizations[0].user_affiliation.has_requested_paid_access
    );

    return (
      <div className={cx('OrganizationTeacherUpsell UnpaidOrganizationsNull', className)}>
        <div className="UnpaidOrganizationsNull--content">
          <Row size="xxxlarge">
            <OrganizationUpsellHeader userRole="teacher" />
          </Row>
          <Row size="small">
            <Container size="xxsmall">
              <Buttons axis="y">
                {!loading && this.renderRequestAccessButton(hasRequestedPaidAccess)}
                <Button
                  level="text"
                  color="mascot"
                  weight="semibold"
                  href={LinkConstants.PLANS}
                  target="_blank"
                  eventKey="admin_first_onboarding.UnpaidOrganizationsNull.teacher.learn_more.click">
                  Learn more
                </Button>
              </Buttons>
            </Container>
          </Row>
          {!loading && hasRequestedPaidAccess && (
            <Container size="small">
              <Row size="xxxlarge">
                <Text color="graphite">
                  School Plans can only be purchased by an admin. Refer your admin, and the Remind
                  team will take care of the rest!
                </Text>
              </Row>
            </Container>
          )}
        </div>
      </div>
    );
  }
}

const withData = graphql(ORGANIZATION_TEACHER_UPSELL_QUERY, {
  options: ({ organizationUuid }: Props): { variables: OrganizationTeacherUpsellVariables } => ({
    variables: { organizationUuid },
  }),
});

const withMutations = graphql(REQUEST_ACCESS_FOR_PAID_UPSELL_MUTATION, {
  props({ mutate }) {
    return {
      requestAccessForPaidUpsell(input: RequestAccessForPaidUpsellInput) {
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

export { OrganizationTeacherUpsell };
export default withData(withMutations(OrganizationTeacherUpsell));
